import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  Center,
  Stack,
  Text,
  Button,
  FileInput,
  Box,
  Group,
  ThemeIcon,
} from "@mantine/core";
import { DataContext } from "../../context/DataContext";
import { uploadImageToS3 } from "../../helpers";
import useStatusCheck from "../../hooks/useStatusCheck";
import VerificationProcessingScreen from "../../components/verification-processing-screen";
import { IconId } from "@tabler/icons-react";
export default function Upload() {
  const { data, settings, text: textFromApi, dispatch } = useContext(DataContext);
  const [frontValue, setFrontValue] = useState(null);
  const [backValue, setBackValue] = useState(null);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const [frontUploaded, setFrontUploaded] = useState(false);
  const [backUploaded, setBackUploaded] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const text = textFromApi.upload;
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [pendingUpload, setPendingUpload] = useState(null);

  const [terminalStatus, setTerminalStatus] = useState(null);

  const captureTerminal = useCallback((result) => {
    if (!result) return true;
    const status = result.status;
    const processState = result.process_state;
    if (processState === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      return false;
    }
    if (processState === "COMPLETED") {
      setTerminalStatus(status === "PASS" ? "PASS" : "FAIL");
      return false;
    }
    if (status === "PASS") {
      setTerminalStatus("PASS");
      return false;
    }
    if (status === "FAIL") {
      setTerminalStatus("FAIL");
      return false;
    }
    return true;
  }, []);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    dispatch({ type: "SET_STATUS", payload: terminalStatus || "FAIL" });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch]);

  useStatusCheck(
    uploadingPassport ? frontUploaded : frontUploaded || backUploaded,
    false,
    "dcams/upload",
    captureTerminal
  );

  // Handle pending uploads
  useEffect(() => {
    if (pendingUpload) {
      const { suffix, file, setUploaded } = pendingUpload;
      processAndUpload(suffix, file, setUploaded);
      setPendingUpload(null);
    }
  }, [pendingUpload]);

  async function processAndUpload(suffix, file, setUploaded) {
    if (file) {
      console.log('📤 Starting upload for:', suffix);
      setUploading(true);
      setUploadComplete(false);
      
      // Add a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Simulate checking image phase
      console.log('🔍 Checking image...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('📦 Converting to blob...');
      const blob = await file.arrayBuffer();

      // Upload to S3
      console.log('☁️ Uploading to S3...');
      await uploadImageToS3(`${settings.request_id}_dl_${suffix}.jpeg`, blob, settings);

      console.log('✅ Upload complete');
      setUploaded(true);
      setUploadComplete(true);
      
      // Keep showing loading for a moment to show "Finalizing..."
      setTimeout(() => {
        console.log('🏁 Hiding upload loading');
        setUploading(false);
        setUploadComplete(false);
      }, 2000);
    }
  }

  async function handleSubmit() {
    setIsVerifying(true);
    data.settings = settings;
    data.type = "Direct_Upload";
    fetch("https://vx.dcams.app/frontend/dcams/process", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (result) {
        //setIsVerifying(false);
      })
      .catch(function () {
        alert("Something Went Wrong! Please try again.");
      });
  }

  // Show the processing screen during both the S3 file upload phase
  // and the post-submit backend verification — the screen opens on
  // "Uploading your document…" which fits the upload phase naturally
  // and continues seamlessly into the verification stages. resultReady
  // only flips during isVerifying (after submit), so during the
  // uploading phase the screen sits/auto-advances until uploading
  // ends.
  if (uploading || isVerifying) {
    return (
      <VerificationProcessingScreen
        type="document"
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between", // This will push the button to the bottom
        height: "100%", // This should take the full height of its parent
      }}
    >
      <Box>
        <Stack>
          <Group>
            <ThemeIcon size={40} variant="light" radius="xl">
              <IconId size={35} stroke={1} />
            </ThemeIcon>
            <Text fz="xl" fw={700}>
              {text.title} {uploadingPassport ? text.passport : text.id}
            </Text>
          </Group>

          <FileInput
            name="front"
            placeholder="Please select a file"
            accept="image/png,image/jpeg"
            value={frontValue}
            onChange={(file) => {
              setFrontValue(file);
              if (file) {
                setPendingUpload({ suffix: "front", file, setUploaded: setFrontUploaded });
              }
            }}
            label={
              uploading
                ? "Uploading..."
                : uploadingPassport
                  ? text.frontUploadPassportTitle
                  : text.frontUploadTitle
            }
          />
          {!uploadingPassport && (
            <FileInput
              name="back"
              placeholder="Please select a file"
              accept="image/png,image/jpeg"
              value={backValue}
              onChange={(file) => {
                setBackValue(file);
                if (file) {
                  setPendingUpload({ suffix: "back", file, setUploaded: setBackUploaded });
                }
              }}
              label={uploading ? "Uploading..." : text.backUploadTitle}
            />
          )}
        </Stack>
      </Box>
      <Box>
        <Stack>
          <Button
            variant="subtle"
            onClick={() => setUploadingPassport(!uploadingPassport)}
          >
            {uploadingPassport
              ? "I want to upload a License or ID"
              : "I want to upload a passport"}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              uploadingPassport
                ? !frontUploaded
                : !frontUploaded || !backUploaded
            }
          >
            Submit
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
